import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { SEO } from '../../utils/seo';

export default function SafeguardingPolicyPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Safeguarding Policy - LEWIS SECURITY, RISK & COMPLIANCE SOLUTIONS",
    "description": "Safeguarding Policy for LEWIS SECURITY, RISK & COMPLIANCE SOLUTIONS. Our commitment to protecting children, young people and vulnerable adults.",
    "url": `${window.location.origin}/safeguarding-policy`
  };

  return (
    <>
      <SEO
        title="Safeguarding Policy | LEWIS SECURITY North Wales"
        description="Safeguarding Policy for LEWIS SECURITY, RISK & COMPLIANCE SOLUTIONS. Our commitment to protecting children, young people and vulnerable adults in training and consultancy services."
        keywords="safeguarding policy, child protection, vulnerable adults, training safety, duty of care, UK safeguarding"
        schema={schema}
        canonical={`${window.location.origin}/safeguarding-policy`}
      />
      <div className="min-h-screen bg-white font-inter">
        <Navbar />
        
        <main className="pt-24 pb-16">
          <div className="max-w-4xl mx-auto px-6">
            <h1 className="text-4xl font-bold text-gray-900 mb-8 font-poppins">Safeguarding Policy</h1>
            
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-600 mb-8">
                <strong>Last Updated:</strong> {new Date().toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' })}
              </p>

              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 font-poppins">Our Commitment</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Lewis Security, Risk & Compliance Solutions is committed to safeguarding the welfare of children, young people and vulnerable adults who participate in our training courses, consultancy activities or subcontracted services.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 font-poppins">What We Believe</h2>
                <div className="bg-red-50 rounded-xl p-8 border border-red-100">
                  <ul className="space-y-4">
                    <li className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <i className="ri-shield-check-line text-sm text-red-600"></i>
                      </div>
                      <span className="text-gray-700 leading-relaxed">
                        <strong>The safety and wellbeing of individuals is paramount</strong>
                      </span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <i className="ri-shield-check-line text-sm text-red-600"></i>
                      </div>
                      <span className="text-gray-700 leading-relaxed">
                        <strong>All people have the right to protection from harm, abuse and neglect</strong>
                      </span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <i className="ri-shield-check-line text-sm text-red-600"></i>
                      </div>
                      <span className="text-gray-700 leading-relaxed">
                        <strong>Safeguarding is everyone's responsibility</strong>
                      </span>
                    </li>
                  </ul>
                </div>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 font-poppins">Our Aims</h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  We are committed to creating and maintaining safe environments for all individuals who engage with our services. To achieve this, we aim to:
                </p>
                
                <div className="space-y-6">
                  <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-user-search-line text-xl text-red-600"></i>
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2 font-poppins">Appropriate Checks & Training</h3>
                        <p className="text-gray-700 leading-relaxed">
                          Ensure instructors and subcontractors hold appropriate training and checks (where required) to work safely with vulnerable groups.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-shield-star-line text-xl text-red-600"></i>
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2 font-poppins">Safe Working Practices</h3>
                        <p className="text-gray-700 leading-relaxed">
                          Promote safe working practices and safe learning environments across all training and consultancy activities.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-alert-line text-xl text-red-600"></i>
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2 font-poppins">Recognise, Respond & Refer</h3>
                        <p className="text-gray-700 leading-relaxed">
                          Recognise, respond and refer concerns or disclosures appropriately in line with UK safeguarding guidance.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-lock-line text-xl text-red-600"></i>
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2 font-poppins">Confidentiality & Data Security</h3>
                        <p className="text-gray-700 leading-relaxed">
                          Maintain confidentiality and secure handling of sensitive information in accordance with UK GDPR and data protection legislation.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-team-line text-xl text-red-600"></i>
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2 font-poppins">Partner Support</h3>
                        <p className="text-gray-700 leading-relaxed">
                          Support clients and partners to meet their safeguarding obligations through guidance and best practice sharing.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 font-poppins">Reporting Concerns</h2>
                <div className="bg-gray-50 rounded-xl p-8 border border-gray-200">
                  <p className="text-gray-700 leading-relaxed mb-4">
                    If a safeguarding concern is identified during training or consultancy work, it may be reported to the appropriate authority in line with UK guidance and duty to act.
                  </p>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    We take all concerns seriously and will respond promptly and appropriately to ensure the safety and wellbeing of those involved.
                  </p>
                  <div className="bg-white rounded-lg p-6 mt-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-3 font-poppins">Who to Contact</h3>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      If you have a safeguarding concern related to our services, please contact us immediately:
                    </p>
                    <div className="space-y-2">
                      <p className="text-gray-700">
                        <strong>Email:</strong> <a href="mailto:info@jl-security.co.uk" className="text-red-600 hover:text-red-700 font-medium">info@jl-security.co.uk</a>
                      </p>
                      <p className="text-gray-700">
                        <strong>Phone:</strong> <a href="tel:+447834561015" className="text-red-600 hover:text-red-700 font-medium">+44 7834 561015</a>
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 font-poppins">Types of Abuse</h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Safeguarding concerns may include, but are not limited to:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white rounded-lg p-5 border border-gray-200">
                    <h4 className="font-bold text-gray-900 mb-2 font-poppins">Physical Abuse</h4>
                    <p className="text-sm text-gray-600">Hitting, shaking, throwing, burning or other physical harm</p>
                  </div>
                  <div className="bg-white rounded-lg p-5 border border-gray-200">
                    <h4 className="font-bold text-gray-900 mb-2 font-poppins">Emotional Abuse</h4>
                    <p className="text-sm text-gray-600">Persistent emotional maltreatment or bullying</p>
                  </div>
                  <div className="bg-white rounded-lg p-5 border border-gray-200">
                    <h4 className="font-bold text-gray-900 mb-2 font-poppins">Sexual Abuse</h4>
                    <p className="text-sm text-gray-600">Forcing or enticing inappropriate sexual activity</p>
                  </div>
                  <div className="bg-white rounded-lg p-5 border border-gray-200">
                    <h4 className="font-bold text-gray-900 mb-2 font-poppins">Neglect</h4>
                    <p className="text-sm text-gray-600">Failure to meet basic physical or emotional needs</p>
                  </div>
                  <div className="bg-white rounded-lg p-5 border border-gray-200">
                    <h4 className="font-bold text-gray-900 mb-2 font-poppins">Financial Abuse</h4>
                    <p className="text-sm text-gray-600">Theft, fraud or exploitation of finances</p>
                  </div>
                  <div className="bg-white rounded-lg p-5 border border-gray-200">
                    <h4 className="font-bold text-gray-900 mb-2 font-poppins">Discriminatory Abuse</h4>
                    <p className="text-sm text-gray-600">Harassment based on protected characteristics</p>
                  </div>
                </div>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 font-poppins">Training & Awareness</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  All instructors and relevant staff receive appropriate safeguarding training to ensure they can:
                </p>
                <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                  <li>Recognise signs of abuse or neglect</li>
                  <li>Respond appropriately to disclosures</li>
                  <li>Understand their duty to report concerns</li>
                  <li>Maintain appropriate professional boundaries</li>
                  <li>Create safe learning environments</li>
                </ul>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 font-poppins">Internal Procedures</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  A detailed internal safeguarding procedure supports this policy, including:
                </p>
                <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                  <li>Clear reporting pathways and escalation procedures</li>
                  <li>Record keeping and documentation requirements</li>
                  <li>Information sharing protocols</li>
                  <li>Support for those making disclosures</li>
                  <li>Regular policy review and updates</li>
                </ul>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 font-poppins">Useful Contacts</h2>
                <div className="bg-gray-50 rounded-xl p-8 border border-gray-200">
                  <div className="space-y-6">
                    <div>
                      <h4 className="font-bold text-gray-900 mb-2 font-poppins">NSPCC Helpline</h4>
                      <p className="text-gray-700 mb-1">For advice on safeguarding children</p>
                      <p className="text-gray-700">Phone: <a href="tel:08088005000" className="text-red-600 hover:text-red-700 font-medium">0808 800 5000</a></p>
                      <p className="text-gray-700">Email: <a href="mailto:help@nspcc.org.uk" className="text-red-600 hover:text-red-700 font-medium">help@nspcc.org.uk</a></p>
                    </div>
                    
                    <div>
                      <h4 className="font-bold text-gray-900 mb-2 font-poppins">Action on Elder Abuse</h4>
                      <p className="text-gray-700 mb-1">For concerns about vulnerable adults</p>
                      <p className="text-gray-700">Phone: <a href="tel:08088088141" className="text-red-600 hover:text-red-700 font-medium">0808 808 8141</a></p>
                    </div>

                    <div>
                      <h4 className="font-bold text-gray-900 mb-2 font-poppins">Local Authority Safeguarding Teams</h4>
                      <p className="text-gray-700">Contact your local council's safeguarding team for immediate concerns in your area</p>
                    </div>

                    <div>
                      <h4 className="font-bold text-gray-900 mb-2 font-poppins">Emergency Services</h4>
                      <p className="text-gray-700">If someone is in immediate danger, call <a href="tel:999" className="text-red-600 hover:text-red-700 font-medium">999</a></p>
                    </div>
                  </div>
                </div>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 font-poppins">Policy Review</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  This policy is reviewed annually or following any significant safeguarding incident or change in legislation. We are committed to continuous improvement in our safeguarding practices.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 font-poppins">Contact Us</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  If you have any questions about this Safeguarding Policy or wish to report a concern, please contact us:
                </p>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <p className="text-gray-700 mb-2"><strong>LEWIS SECURITY, RISK & COMPLIANCE SOLUTIONS</strong></p>
                  <p className="text-gray-700 mb-2">Email: <a href="mailto:info@jl-security.co.uk" className="text-red-600 hover:text-red-700 font-medium">info@jl-security.co.uk</a></p>
                  <p className="text-gray-700">Phone: <a href="tel:+447834561015" className="text-red-600 hover:text-red-700 font-medium">+44 7834 561015</a></p>
                </div>
              </section>
            </div>

            <div className="mt-12 pt-8 border-t border-gray-200">
              <Link 
                to="/" 
                className="inline-flex items-center text-red-600 hover:text-red-700 font-medium transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line mr-2"></i>
                Back to Home
              </Link>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}
